import { create } from "zustand";

const ACCESS_KEY = "se_access_token";
const REFRESH_KEY = "se_refresh_token";
const USER_KEY = "se_user";

const BASE = import.meta.env.VITE_API_BASE ?? "http://localhost:4000";

export interface AuthUser {
  id: string;
  email: string;
  role: string;
}

interface AuthStore {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  hydrated: boolean;
  setSession: (next: {
    accessToken: string;
    refreshToken: string;
    user?: AuthUser;
  }) => void;
  setUser: (user: AuthUser | null) => void;
  clear: () => void;
  hydrate: () => void;
}

function readStored<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export const useAuth = create<AuthStore>((set) => ({
  accessToken: null,
  refreshToken: null,
  user: null,
  hydrated: false,
  setSession: (next) => {
    localStorage.setItem(ACCESS_KEY, next.accessToken);
    localStorage.setItem(REFRESH_KEY, next.refreshToken);
    if (next.user) localStorage.setItem(USER_KEY, JSON.stringify(next.user));
    set({
      accessToken: next.accessToken,
      refreshToken: next.refreshToken,
      user: next.user ?? null,
      hydrated: true,
    });
  },
  setUser: (user) => {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
    set({ user });
  },
  clear: () => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    set({ accessToken: null, refreshToken: null, user: null });
  },
  hydrate: () => {
    const accessToken = localStorage.getItem(ACCESS_KEY);
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    const user = readStored<AuthUser>(USER_KEY);
    set({ accessToken, refreshToken, user, hydrated: true });
  },
}));

/**
 * Authenticated fetch. Adds Bearer token from the auth store and clears
 * the session on a 401, letting the route guard redirect to /signin.
 */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const url = path.startsWith("http") ? path : `${BASE}${path}`;
  const token = useAuth.getState().accessToken;
  const headers = new Headers(init.headers ?? {});
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(url, { ...init, headers });
  if (res.status === 401 && token) {
    useAuth.getState().clear();
  }
  return res;
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
}

export async function loginRequest(
  email: string,
  password: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(`${BASE}/v1/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    return { ok: false, error: body.error ?? `error_${res.status}` };
  }
  const tokens = (await res.json()) as LoginResponse;
  useAuth.getState().setSession({ ...tokens });

  // Hydrate user profile.
  const meRes = await fetch(`${BASE}/v1/auth/me`, {
    headers: { Authorization: `Bearer ${tokens.accessToken}` },
  });
  if (meRes.ok) {
    const me = (await meRes.json()) as { sub: string; email: string; role: string };
    useAuth
      .getState()
      .setUser({ id: me.sub, email: me.email, role: me.role });
  }
  return { ok: true };
}

export async function registerRequest(
  email: string,
  password: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await fetch(`${BASE}/v1/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    return { ok: false, error: body.error ?? `error_${res.status}` };
  }
  return { ok: true };
}

export function logout(): void {
  useAuth.getState().clear();
}
